import { NotificationLevel, NotificationMessage, NotificationOptions } from './types';

class NotificationService {
  private listeners: Set<(notifications: NotificationMessage[]) => void> = new Set();
  private notifications: NotificationMessage[] = [];

  private generateId(): string {
    return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private logToConsole(message: string, level: NotificationLevel): void {
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] ${level.toUpperCase()}: ${message}`;

    switch (level) {
      case 'info':
        console.info(logMessage);
        break;
      case 'success':
        console.log(`✅ ${logMessage}`);
        break;
      case 'warning':
        console.warn(`⚠️ ${logMessage}`);
        break;
      case 'error':
        console.error(`❌ ${logMessage}`);
        break;
      default:
        console.log(logMessage);
    }
  }

  private getDefaultAutoHideDuration(level: NotificationLevel): number {
    switch (level) {
      case 'error':
        return 8000; // 8 seconds for errors
      case 'warning':
        return 6000; // 6 seconds for warnings
      case 'success':
        return 4000; // 4 seconds for success
      case 'info':
      default:
        return 5000; // 5 seconds for info
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener([...this.notifications]));
  }

  public addNotification(message: string, options: NotificationOptions = {}): string {
    const { level = 'info', autoHideDuration, persist = false } = options;

    // Log to console first
    this.logToConsole(message, level);

    const notification: NotificationMessage = {
      id: this.generateId(),
      message,
      level,
      timestamp: new Date(),
      autoHideDuration: persist
        ? undefined
        : (autoHideDuration ?? this.getDefaultAutoHideDuration(level)),
    };

    this.notifications.push(notification);
    this.notifyListeners();

    // Auto-remove notification if not persistent
    if (!persist && notification.autoHideDuration) {
      setTimeout(() => {
        this.removeNotification(notification.id);
      }, notification.autoHideDuration);
    }

    return notification.id;
  }

  public removeNotification(id: string): void {
    this.notifications = this.notifications.filter((n) => n.id !== id);
    this.notifyListeners();
  }

  public clearAll(): void {
    this.notifications = [];
    this.notifyListeners();
  }

  public subscribe(listener: (notifications: NotificationMessage[]) => void): () => void {
    this.listeners.add(listener);
    // Immediately call with current notifications
    listener([...this.notifications]);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  // Convenience methods
  public info(message: string, options?: Omit<NotificationOptions, 'level'>): string {
    return this.addNotification(message, { ...options, level: 'info' });
  }

  public success(message: string, options?: Omit<NotificationOptions, 'level'>): string {
    return this.addNotification(message, { ...options, level: 'success' });
  }

  public warning(message: string, options?: Omit<NotificationOptions, 'level'>): string {
    return this.addNotification(message, { ...options, level: 'warning' });
  }

  public error(message: string, options?: Omit<NotificationOptions, 'level'>): string {
    return this.addNotification(message, { ...options, level: 'error' });
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
